import React, { useCallback, useEffect, useState } from 'react'
import type { TaskEntryData } from '../TaskEntry'
import { TaskDropdown } from '../TaskDropdown'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/src/renderer/components/ui/tooltip'
import { ReactivateIcon } from '@/src/renderer/components/icons/ReactivateIcon'
import { ReactivateMenuList } from './ReactivateMenuList'
import { useReactivateAction } from './useReactivateAction'
import type { StagedAction } from '../../utils/buildTaskUpdate'
import './TaskReactivateButton.scss'

interface TaskReactivateButtonProps {
  /** The dormant task this button acts on. Carries the id (for db
   *  writes), projectId (gates the "Wake in project" entry), and
   *  lifeSpace (scopes the "Move to project" picker). */
  task?: TaskEntryData
  /** Receives the original mouse event so callers can drive selection
   *  (single-select clears other selections, Cmd/Shift toggle, etc.). */
  onClick?: (event: React.MouseEvent) => void
  ariaLabel?: string
  /** Controlled-mode dropdown state. When `open` is undefined the
   *  button manages its own state (default). Pass both to let a
   *  parent (e.g. TaskEntry) drive the dropdown — used when the
   *  TaskContextMenu's "Aufwecken" entry needs to open it from outside. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** When true, clicking the button skips the dropdown and directly
   *  wakes the task (`pausedAt = null`). Used on pages where the user
   *  is already viewing the task's project — opening the picker would
   *  add an extra step without offering anything actionable beyond
   *  "wake here". */
  directWake?: boolean
  /** Required when `directWake` is on — handler for the immediate wake
   *  write. Hosted by the parent (TaskEntry) so the write can flow
   *  through the page's optimistic-update sink and the row flips out
   *  of dormant state on next paint instead of after the IDB →
   *  LiveQuery roundtrip. */
  onDirectWake?: () => void
}

export const TaskReactivateButton: React.FC<TaskReactivateButtonProps> = ({
  task,
  onClick,
  ariaLabel,
  open: openProp,
  onOpenChange,
  directWake = false,
  onDirectWake,
}) => {
  const [openInternal, setOpenInternal] = useState(false)
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : openInternal
  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(open) : next
    if (!isControlled) setOpenInternal(value)
    onOpenChange?.(value)
  }
  // Internal hover/focus state for the tooltip — gated by `open` so the
  // tooltip never shows while the dropdown is open (otherwise we'd
  // stack two floaters over the same button).
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const commit = useReactivateAction(task)

  const handleAction = useCallback(
    (action: StagedAction) => {
      void commit(action)
    },
    [commit],
  )

  // Outer wrapper handles row-selection + bubble-stop. The actual
  // dropdown toggle is wired by `TaskDropdown`'s `useClick` middleware
  // on the inner <button> — `cloneElement` overrides any onClick we put
  // there, so we hook into the wrapper's bubble phase instead.
  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick?.(e)
  }

  // Cmd/Ctrl shortcuts — only active while the dropdown is open. Mirrors
  // the shortcut chips in the menu (⌘H/⌘M/⌘L). "wakeInProject" and the
  // nested sub-dropdowns (later/move) intentionally have no direct
  // shortcut: opening their picker requires a UI click.
  useEffect(() => {
    if (!open || !task) return

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return
      }
      if (!e.metaKey && !e.ctrlKey) return
      if (e.altKey || e.shiftKey) return

      let action: StagedAction | null = null
      switch (e.key.toLowerCase()) {
        case 'h':
          action = { kind: 'today' }
          break
        case 'm':
          action = { kind: 'tomorrow' }
          break
        case 'l':
          action = { kind: 'delete' }
          break
        // 'd' (later) and 'v' (move) require a sub-dropdown picker —
        // skipping them here keeps the keyboard contract honest.
      }
      if (!action) return

      e.preventDefault()
      handleAction(action)
      setOpen(false)
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, task, handleAction])

  // direct-wake mode: skip the dropdown entirely. The button calls the
  // parent-supplied `onDirectWake` so the write can flow through the
  // page's optimistic-update sink (no IDB → LiveQuery roundtrip before
  // the row flips out of dormant state).
  if (directWake) {
    return (
      <TooltipProvider delayDuration={400}>
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <span
              className="task-reactivate-button-wrap"
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="task-reactivate-button"
                aria-label={ariaLabel}
                onClick={(e) => {
                  e.stopPropagation()
                  onClick?.(e)
                  onDirectWake?.()
                }}
              >
                <ReactivateIcon className="task-reactivate-button__icon" size={16} />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" align="center">
            {ariaLabel}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip open={tooltipOpen && !open} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <span
            className="task-reactivate-button-wrap"
            onClick={handleWrapperClick}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <TaskDropdown
              open={open}
              onOpenChange={setOpen}
              side="bottom"
              align="end"
              sideOffset={2}
              trigger={
                <button
                  type="button"
                  className={`task-reactivate-button${open ? ' task-reactivate-button--open' : ''}`}
                  aria-label={ariaLabel}
                  aria-haspopup="menu"
                  aria-expanded={open}
                >
                  <ReactivateIcon className="task-reactivate-button__icon" size={16} />
                </button>
              }
            >
              {task && (
                <ReactivateMenuList
                  task={task}
                  onAction={handleAction}
                  onClose={() => setOpen(false)}
                />
              )}
            </TaskDropdown>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          {ariaLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
